/**
 * tableConditionalFormat.ts
 *
 * Value-threshold conditional formatting for the Table widget (#969 Part C,
 * e.g. "cell % > 50 -> green background"). Pure and side-effect free — no
 * React, no fetch — called from platinum-table-new/styles.ts's
 * getBackgroundColor/getTextColor, the "clean conditional-formatting seams"
 * per the D3 brief.
 *
 * Rule model deliberately small: comparator + threshold + colour, list of
 * rules, first match wins. See TableConditionalFormatRule in
 * goapi/pkg/types/types.go for the mirrored Go doc.
 *
 * Value extraction: a data cell's value is read from its band — colpc/rowpc
 * are converted to a 0-100 percentage magnitude (matching how the vast
 * majority of exec tables display them, and the issue's own example,
 * "green >= 50%, red <= 20%"), freq is used as-is. This does NOT consult
 * PlatinumProperties.cells.percentsAsProportions — that setting lives on the
 * table's render config, not on the per-cell style seam
 * (getBackgroundColor/getTextColor only receive a GridCell), so a rule
 * threshold always means "displayed-percentage scale" regardless of that
 * setting. Documented simplification, not a bug: see #969 design controls.
 */

import type { GridCell } from '../components/platinum-table-new/layout'
import type { TableConditionalFormatRule } from '../types/gotypes'

/** Extracts the numeric value a conditional-formatting rule should evaluate for this cell, or undefined if the cell has no comparable value. */
export function resolveCellNumericValue(cell: GridCell): number | undefined {
  if (cell.type !== 'data' || !cell.sourceData) return undefined
  const band = cell.bandStyle?.band

  switch (band) {
    case 'colpc':
      return cell.sourceData.colpc != null ? cell.sourceData.colpc * 100 : undefined
    case 'rowpc':
      return cell.sourceData.rowpc != null ? cell.sourceData.rowpc * 100 : undefined
    case 'freq':
      return cell.sourceData.freq
    default:
      return undefined
  }
}

/**
 * True when `value` meets one rule's comparator and threshold. Shared with
 * the bar-chart colour rules (#1808, applyChartDesign.ts).
 */
export function ruleMatches(value: number, rule: TableConditionalFormatRule): boolean {
  switch (rule.comparator) {
    case 'gt':
      return value > rule.threshold
    case 'gte':
      return value >= rule.threshold
    case 'lt':
      return value < rule.threshold
    case 'lte':
      return value <= rule.threshold
    case 'eq':
      return value === rule.threshold
    case 'between': {
      const { threshold: a, threshold_upper: b } = rule
      if (typeof b !== 'number' || Number.isNaN(b) || Number.isNaN(a)) return false
      return value >= Math.min(a, b) && value <= Math.max(a, b)
    }
    default:
      return false
  }
}

/**
 * Returns the rule with a new comparator. Leaving "between" drops the upper
 * bound; entering it seeds the upper bound from the threshold, so the rule
 * never holds a missing or NaN bound.
 */
export function withComparator(
  rule: TableConditionalFormatRule,
  comparator: TableConditionalFormatRule['comparator'],
): TableConditionalFormatRule {
  const { threshold_upper, ...rest } = rule
  if (comparator === 'between') return { ...rest, comparator, threshold_upper: threshold_upper ?? rule.threshold }
  return { ...rest, comparator }
}

export interface ConditionalFormatResult {
  backgroundColor?: string
  textColor?: string
}

/**
 * Evaluates a cell's value against an ordered list of rules — first match
 * wins. Returns undefined when there is no value to compare (non-data
 * cells, or a band with no comparable figure) or no rule matches.
 */
export function evaluateConditionalFormat(
  cell: GridCell,
  rules: TableConditionalFormatRule[] | undefined,
): ConditionalFormatResult | undefined {
  if (!rules || rules.length === 0) return undefined
  const value = resolveCellNumericValue(cell)
  if (value === undefined) return undefined

  for (const rule of rules) {
    if (ruleMatches(value, rule)) {
      const result: ConditionalFormatResult = {}
      if (rule.background_color) result.backgroundColor = rule.background_color
      if (rule.text_color) result.textColor = rule.text_color
      if (result.backgroundColor === undefined && result.textColor === undefined) continue
      return result
    }
  }
  return undefined
}
